import { useEffect, useState } from "react";
import { AdminLayout } from "../components/admin/AdminLayout";
import { StatsCard } from "../components/admin/StatsCard";
import { Card } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { adminApi, type AdminStats, type Owner } from "../lib/adminApi";
import { useToast } from "../hooks/use-toast";
import {
  TrendingUp,
  TrendingDown,
  RefreshCw,
  AlertCircle,
  CheckCircle,
  Clock,
  PauseCircle,
  DollarSign,
  Download,
  Send,
  Loader2,
} from "lucide-react";
import { Badge } from "../components/ui/badge";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Bell } from "lucide-react";
import { AdminPasswordChange } from "../components/admin/AdminPasswordChange";

// Componente del contenido del modal
// Componente del contenido del modal
function NotificationModalContent({
  owners,
  onClose,
}: {
  owners: Owner[];
  onClose: () => void;
}) {
  const [selectedOwnerId, setSelectedOwnerId] = useState<string>("all");
  const [message, setMessage] = useState("");
  const [title, setTitle] = useState("");
  const [notificationType, setNotificationType] = useState("info");
  const [isSending, setIsSending] = useState(false);
  const { toast } = useToast();

  const handleSend = async () => {
    if (!title.trim() || !message.trim()) {
      toast({
        title: "Error",
        description: "El título y mensaje son requeridos",
        variant: "destructive",
      });
      return;
    }

    setIsSending(true);
    try {
      const ownerIds = selectedOwnerId === "all" ? "all" : [selectedOwnerId];

      const result = await adminApi.sendOwnerNotification({
        ownerIds,
        title,
        message,
        type: notificationType as any,
      });

      toast({
        title: "✅ Notificaciones enviadas",
        description: result.message,
      });

      // Limpiar formulario
      setMessage("");
      setTitle("");
      setSelectedOwnerId("all");

      // Cerrar el modal usando la prop onClose
      onClose();
    } catch (error: any) {
      toast({
        title: "❌ Error",
        description:
          error.message || "No se pudieron enviar las notificaciones",
        variant: "destructive",
      });
    } finally {
      setIsSending(false);
    }
  };

  return (
    <>
      <div className="space-y-4 py-4">
        {/* Título */}
        <div className="space-y-2">
          <Label htmlFor="title">Título *</Label>
          <input
            id="title"
            value={title}
            onChange={(e) => setTitle(e.target.value)}
            placeholder="Ej: Recordatorio de pago"
            className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background file:border-0 file:bg-transparent file:text-sm file:font-medium placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50"
          />
        </div>

        {/* Selección de negocios */}
        <div className="space-y-2">
          <Label>Destinatario *</Label>
          <Select value={selectedOwnerId} onValueChange={setSelectedOwnerId}>
            <SelectTrigger>
              <SelectValue placeholder="Selecciona destinatario..." />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Todos los negocios</SelectItem>
              {owners.map((owner) => (
                <SelectItem key={owner.id} value={owner.id}>
                  {owner.businessName} ({owner.accountStatus}) -{" "}
                  {owner.planType}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        {/* Tipo de notificación */}
        <div className="space-y-2">
          <Label>Tipo de notificación</Label>
          <Select value={notificationType} onValueChange={setNotificationType}>
            <SelectTrigger>
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="info">ℹ️ Información</SelectItem>
              <SelectItem value="warning">⚠️ Advertencia</SelectItem>
              <SelectItem value="error">🔴 Error/Crítico</SelectItem>
              <SelectItem value="payment">💰 Pago</SelectItem>
              <SelectItem value="system">⚙️ Sistema</SelectItem>
            </SelectContent>
          </Select>
        </div>

        {/* Mensaje */}
        <div className="space-y-2">
          <Label htmlFor="message">Mensaje *</Label>
          <Textarea
            id="message"
            placeholder="Escribe tu mensaje aquí..."
            value={message}
            onChange={(e) => setMessage(e.target.value)}
            rows={4}
          />
          <p className="text-sm text-muted-foreground">
            {message.length}/500 caracteres
          </p>
        </div>
      </div>

      <DialogFooter>
        <Button variant="outline" onClick={onClose}>
          Cancelar
        </Button>
        <Button
          onClick={handleSend}
          disabled={isSending || !message.trim() || !title.trim()}
        >
          {isSending ? (
            <>
              <Loader2 className="mr-2 h-4 w-4 animate-spin" />
              Enviando...
            </>
          ) : (
            "Enviar Notificación"
          )}
        </Button>
      </DialogFooter>
    </>
  );
}

export default function AdminDashboard() {
  const [stats, setStats] = useState<AdminStats | null>(null);
  const [loading, setLoading] = useState(true);
  const [owners, setOwners] = useState<Owner[]>([]);
  const { toast } = useToast();
  const [recentActivities, setRecentActivities] = useState<any[]>([]);
  const [notificationModalOpen, setNotificationModalOpen] = useState(false); // ← AGREGAR ESTA LÍNEA
  // Estado para paginación
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(5);

  const fetchStats = async () => {
    try {
      setLoading(true);
      const data = await adminApi.getStats();
      setStats(data);
    } catch (error) {
      toast({
        title: "Error",
        description: "No se pudieron cargar las estadísticas",
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchStats();
  }, []);

  // Cargar actividades recientes desde owners
  useEffect(() => {
    const loadRecentActivities = async () => {
      try {
        // Cargar todos los owners
        const ownersData = await adminApi.getOwners({ limit: 100 });
        setOwners(ownersData.data);

        // Crear actividades a partir de los owners más recientes
        const recentOwners = [...ownersData.data]
          .sort(
            (a, b) =>
              new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime(),
          )
          .slice(0, 5);

        // Transformar owners en actividades
        const activities = recentOwners.map((owner) => {
          let activityType = "create_owner";
          let icon = CheckCircle;
          let iconColor = "text-green-600";
          let bgColor = "bg-green-100";
          let badgeText = "Nuevo";
          let description = "";

          if (owner.accountStatus === "suspended") {
            activityType = "suspend_owner";
            icon = PauseCircle;
            iconColor = "text-red-600";
            bgColor = "bg-red-100";
            badgeText = "Suspendido";
            description = "Cuenta suspendida";
          } else if (owner.accountStatus === "active") {
            description = "Cuenta activa";
          } else if (owner.accountStatus === "trial") {
            activityType = "trial_owner";
            icon = Clock;
            iconColor = "text-yellow-600";
            bgColor = "bg-yellow-100";
            badgeText = "Trial";
            description = "En período de prueba";

            // Calcular días restantes de trial
            if (owner.trialEndDate) {
              const endDate = new Date(owner.trialEndDate);
              const today = new Date();
              today.setHours(0, 0, 0, 0);
              endDate.setHours(0, 0, 0, 0);
              const timeDiff = endDate.getTime() - today.getTime();
              const daysRemaining = Math.max(
                0,
                Math.floor(timeDiff / (1000 * 60 * 60 * 24)),
              );
              description = `Trial: ${daysRemaining} día${daysRemaining !== 1 ? "s" : ""} restante${daysRemaining !== 1 ? "s" : ""}`;
            }
          }

          // Detectar si es muy reciente (menos de 24 horas)
          const ownerDate = new Date(owner.createdAt);
          const now = new Date();
          const hoursSinceCreation = Math.floor(
            (now.getTime() - ownerDate.getTime()) / (1000 * 60 * 60),
          );
          const isVeryRecent = hoursSinceCreation < 24;

          return {
            id: owner.id,
            type: activityType,
            title: `${isVeryRecent ? "🆕 " : ""}Negocio: ${owner.businessName}`,
            description:
              description || `${owner.ownerName} • ${owner.planType}`,
            date: owner.createdAt,
            icon,
            iconColor,
            bgColor,
            badgeColor:
              activityType === "trial_owner"
                ? "bg-yellow-100 text-yellow-800"
                : activityType === "suspend_owner"
                  ? "bg-red-100 text-red-800"
                  : "bg-green-100 text-green-800",
            badgeText: isVeryRecent ? "Nuevo" : badgeText,
            admin: "Sistema",
            details: {
              businessName: owner.businessName,
              ownerName: owner.ownerName,
              planType: owner.planType,
              accountStatus: owner.accountStatus,
              isVeryRecent,
            },
          };
        });

        setRecentActivities(activities);
      } catch (error) {
        console.error("Error cargando actividades:", error);
      }
    };

    loadRecentActivities();
  }, []);

  // Resetear a página 1 cuando cambian las actividades
  useEffect(() => {
    setCurrentPage(1);
  }, [recentActivities.length]);

  // Función para obtener título de actividad - CORREGIDO
  // Función para obtener título de actividad
  const getActivityTitle = (actionType: string, details: any) => {
    switch (actionType) {
      case "create_owner":
        return `Negocio creado: ${details?.businessName || "Nuevo negocio"}`;
      case "suspend_owner":
        return `Negocio suspendido: ${details?.businessName || "Negocio"}`;
      case "extend_trial":
        return `Trial extendido: ${details?.businessName || "Negocio"}`;
      case "activate_owner":
        return `Negocio activado: ${details?.businessName || "Negocio"}`;
      default:
        return "Actividad del sistema";
    }
  };

  // Función para obtener descripción de actividad
  const getActivityDescription = (actionType: string, details: any) => {
    switch (actionType) {
      case "create_owner":
        return `${details?.ownerName || "Dueño no especificado"} • Plan ${details?.planType || "básico"}`;
      case "suspend_owner":
        return `Motivo: ${details?.reason || "Trial expirado"}`;
      case "extend_trial":
        return `+${details?.days || 0} días adicionales`;
      case "activate_owner":
        return `Cuenta activada • Próximo pago: ${details?.nextPaymentDate || "N/A"}`;
      default:
        return "Sin detalles";
    }
  };

  // Función para formatear fecha relativa
  const formatTimeAgo = (dateString: string) => {
    const date = new Date(dateString);
    const now = new Date();
    const diffMs = now.getTime() - date.getTime();
    const diffHours = Math.floor(diffMs / (1000 * 60 * 60));

    if (diffHours < 1) {
      const diffMinutes = Math.floor(diffMs / (1000 * 60));
      return `Hace ${diffMinutes} minuto${diffMinutes !== 1 ? "s" : ""}`;
    } else if (diffHours < 24) {
      return `Hace ${diffHours} hora${diffHours !== 1 ? "s" : ""}`;
    } else {
      const diffDays = Math.floor(diffHours / 24);
      return `Hace ${diffDays} día${diffDays !== 1 ? "s" : ""}`;
    }
  };

  const handleExportData = () => {
    toast({
      title: "Datos exportados",
      description: "Los datos se han exportado exitosamente",
    });
  };

  // Calcular actividades para la página actual
  const getPaginatedActivities = () => {
    const startIndex = (currentPage - 1) * itemsPerPage;
    const endIndex = startIndex + itemsPerPage;
    return recentActivities.slice(startIndex, endIndex);
  };

  const totalPages = Math.ceil(recentActivities.length / itemsPerPage);

  // Funciones para cambiar de página
  const goToNextPage = () => {
    if (currentPage < totalPages) {
      setCurrentPage(currentPage + 1);
    }
  };

  const goToPrevPage = () => {
    if (currentPage > 1) {
      setCurrentPage(currentPage - 1);
    }
  };

  const goToPage = (page: number) => {
    if (page >= 1 && page <= totalPages) {
      setCurrentPage(page);
    }
  };

  return (
    <AdminLayout title="Dashboard">
      {/* Header Actions */}
      <div className="flex flex-col lg:flex-row lg:items-center justify-between gap-4 mb-6">
        <div>
          <h2 className="text-lg font-semibold text-gray-900">
            Resumen General
          </h2>
          <p className="text-gray-600">
            Estadísticas en tiempo real del sistema
          </p>
        </div>
        <div className="flex flex-wrap gap-2">
          {/* Botón de Enviar Notificaciones */}
          <Button
            variant="outline"
            onClick={() => setNotificationModalOpen(true)}
          >
            <Send className="h-4 w-4 mr-2" />
            Enviar Notificaciones
          </Button>
          <Button variant="outline" onClick={handleExportData}>
            <Download className="h-4 w-4 mr-2" />
            Exportar Datos
          </Button>
          <Button onClick={fetchStats} disabled={loading}>
            <RefreshCw
              className={`h-4 w-4 mr-2 ${loading ? "animate-spin" : ""}`}
            />
            Actualizar
          </Button>
        </div>
      </div>

      {/* Stats Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
        <StatsCard
          title="Total Negocios"
          value={stats?.totalShops || 0}
          description="Registrados en el sistema"
          icon="total"
          loading={loading}
        />

        <StatsCard
          title="Activos"
          value={stats?.activeShops || 0}
          description="Con suscripción pagada"
          icon="active"
          trend={5}
          loading={loading}
        />

        <StatsCard
          title="En Trial"
          value={stats?.trialShops || 0}
          description="Período de prueba"
          icon="trial"
          loading={loading}
        />

        <StatsCard
          title="Suspendidos"
          value={stats?.suspendedShops || 0}
          description="Pendientes de pago"
          icon="suspended"
          trend={-2}
          loading={loading}
        />
      </div>

      {/* Second Row Stats */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-8">
        <StatsCard
          title="Por Vencer"
          value={stats?.expiringSoon || 0}
          description="Suscripciones en 5 días"
          icon="expiring"
          loading={loading}
        />

        <StatsCard
          title="Vencidos"
          value={stats?.overdueShops || 0}
          description="Pagos atrasados"
          icon="overdue"
          loading={loading}
        />

        <StatsCard
          title="Ingresos Mes"
          value={`${stats?.monthlyRevenue || 0} CUP`}
          description="Total del mes actual"
          icon="revenue"
          trend={12}
          loading={loading}
        />
      </div>

      {/* Recent Activity - Full Width */}
      <div className="col-span-full">
        {/* Actividad Reciente - DATOS REALES */}

        <Card className="p-4 sm:p-6">
          <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 mb-6">
            <h3 className="text-lg font-semibold text-gray-900">
              Actividad Reciente
            </h3>
            <div className="flex flex-wrap gap-2">
              <Badge
                variant="outline"
                className="bg-green-50 text-green-700 border-green-200 hover:bg-green-100 text-xs"
              >
                <CheckCircle className="h-3 w-3 mr-1" /> Activas
              </Badge>
              <Badge
                variant="outline"
                className="bg-yellow-50 text-yellow-700 border-yellow-200 hover:bg-yellow-100 text-xs"
              >
                <Clock className="h-3 w-3 mr-1" /> Trials
              </Badge>
              <Badge
                variant="outline"
                className="bg-red-50 text-red-700 border-red-200 hover:bg-red-100 text-xs"
              >
                <PauseCircle className="h-3 w-3 mr-1" /> Suspendidas
              </Badge>
              <Badge
                variant="outline"
                className="bg-blue-50 text-blue-700 border-blue-200 hover:bg-blue-100 text-xs"
              >
                <span className="text-xs mr-1">🆕</span> Recientes
              </Badge>
            </div>
          </div>

          {recentActivities.length === 0 ? (
            <div className="text-center py-8">
              <div className="w-12 h-12 mx-auto mb-3 rounded-full bg-gray-100 flex items-center justify-center">
                <Clock className="h-6 w-6 text-gray-400" />
              </div>
              <p className="text-gray-500">No hay actividades recientes</p>
              <p className="text-sm text-gray-400 mt-1">
                Las actividades aparecerán aquí automáticamente
              </p>
            </div>
          ) : (
            <div className="space-y-3">
              {getPaginatedActivities().map((activity) => (
                <div
                  key={activity.id}
                  className="flex items-start justify-between p-3 sm:p-4 bg-gray-50 rounded-lg hover:bg-gray-100 transition-colors gap-3"
                >
                  <div className="flex items-start flex-1 min-w-0">
                    <div
                      className={`w-8 h-8 sm:w-10 sm:h-10 ${activity.bgColor} rounded-full flex items-center justify-center mr-3 shrink-0`}
                    >
                      <activity.icon
                        className={`h-4 w-4 sm:h-5 sm:w-5 ${activity.iconColor}`}
                      />
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-start justify-between gap-2">
                        <div className="flex-1 min-w-0">
                          <p className="font-medium text-gray-900 text-sm sm:text-base truncate">
                            {activity.title}
                          </p>
                          <p className="text-xs sm:text-sm text-gray-600 truncate mt-1">
                            {activity.description}
                          </p>
                        </div>
                        {/* Fecha en móvil - a la derecha del título */}
                        <div className="sm:hidden flex flex-col text-right shrink-0 ml-2">
                          <p className="text-xs font-medium text-gray-700 whitespace-nowrap">
                            {formatTimeAgo(activity.date)}
                          </p>
                          <p className="text-xs text-gray-500 whitespace-nowrap">
                            {new Date(activity.date).toLocaleDateString(
                              "es-ES",
                              {
                                day: "2-digit",
                                month: "short",
                              },
                            )}
                          </p>
                        </div>
                      </div>
                      <div className="flex items-center flex-wrap gap-1 sm:gap-2 mt-2">
                        <Badge
                          variant="outline"
                          className={`text-xs ${activity.badgeColor} border-transparent`}
                        >
                          {activity.badgeText}
                        </Badge>
                        <span className="text-xs text-gray-500 truncate">
                          por {activity.admin}
                        </span>
                      </div>
                    </div>
                  </div>
                  {/* Fecha en desktop - se mantiene igual */}
                  <div className="hidden sm:flex flex-col text-right shrink-0 ml-4">
                    <p className="text-sm font-medium text-gray-700 whitespace-nowrap">
                      {formatTimeAgo(activity.date)}
                    </p>
                    <p className="text-xs text-gray-500 whitespace-nowrap">
                      {new Date(activity.date).toLocaleDateString("es-ES", {
                        day: "2-digit",
                        month: "short",
                      })}
                    </p>
                  </div>
                </div>
              ))}
            </div>
          )}

          {/* Leyenda de colores */}
          <div className="mt-6 pt-4 border-t border-gray-200">
            <p className="text-xs text-gray-500 mb-2">Leyenda de estados:</p>
            <div className="flex flex-wrap gap-3">
              <div className="flex items-center">
                <div className="w-3 h-3 rounded-full bg-green-100 border border-green-300 mr-2"></div>
                <div>
                  <span className="text-xs font-medium text-gray-700">
                    Negocio Activo
                  </span>
                  <div className="text-xs text-gray-500">
                    Suscripción pagada
                  </div>
                </div>
              </div>
              <div className="flex items-center">
                <div className="w-3 h-3 rounded-full bg-yellow-100 border border-yellow-300 mr-2"></div>
                <div>
                  <span className="text-xs font-medium text-gray-700">
                    Negocio en Trial
                  </span>
                  <div className="text-xs text-gray-500">Período de prueba</div>
                </div>
              </div>
              <div className="flex items-center">
                <div className="w-3 h-3 rounded-full bg-red-100 border border-red-300 mr-2"></div>
                <div>
                  <span className="text-xs font-medium text-gray-700">
                    Negocio Suspendido
                  </span>
                  <div className="text-xs text-gray-500">Pago pendiente</div>
                </div>
              </div>
            </div>
            <div className="mt-3 flex items-center">
              <span className="text-xs mr-2">🆕</span>
              <span className="text-xs text-gray-500">
                Negocio creado en las últimas 24 horas
              </span>
            </div>
          </div>

          {/* Controles de paginación */}
          {recentActivities.length > itemsPerPage && (
            <div className="flex flex-col sm:flex-row items-center justify-between gap-3 mt-6 pt-4 border-t border-gray-200">
              <div className="text-sm text-gray-600">
                Mostrando {(currentPage - 1) * itemsPerPage + 1} -{" "}
                {Math.min(currentPage * itemsPerPage, recentActivities.length)}{" "}
                de {recentActivities.length} actividades
              </div>

              <div className="flex items-center gap-2">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={goToPrevPage}
                  disabled={currentPage === 1}
                  className="h-8 w-8 p-0"
                >
                  <span className="sr-only">Anterior</span>←
                </Button>

                {/* Números de página */}
                <div className="flex items-center gap-1">
                  {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
                    let pageNum;
                    if (totalPages <= 5) {
                      pageNum = i + 1;
                    } else if (currentPage <= 3) {
                      pageNum = i + 1;
                    } else if (currentPage >= totalPages - 2) {
                      pageNum = totalPages - 4 + i;
                    } else {
                      pageNum = currentPage - 2 + i;
                    }

                    return (
                      <Button
                        key={pageNum}
                        variant={
                          currentPage === pageNum ? "default" : "outline"
                        }
                        size="sm"
                        className="h-8 w-8 p-0 text-xs"
                        onClick={() => goToPage(pageNum)}
                      >
                        {pageNum}
                      </Button>
                    );
                  })}

                  {totalPages > 5 && currentPage < totalPages - 2 && (
                    <>
                      <span className="text-gray-400">...</span>
                      <Button
                        variant="outline"
                        size="sm"
                        className="h-8 w-8 p-0 text-xs"
                        onClick={() => goToPage(totalPages)}
                      >
                        {totalPages}
                      </Button>
                    </>
                  )}
                </div>

                <Button
                  variant="outline"
                  size="sm"
                  onClick={goToNextPage}
                  disabled={currentPage === totalPages}
                  className="h-8 w-8 p-0"
                >
                  <span className="sr-only">Siguiente</span>→
                </Button>
              </div>

              {/* Selector de items por página */}
              <div className="flex items-center gap-2">
                <span className="text-sm text-gray-600">Mostrar:</span>
                <select
                  value={itemsPerPage}
                  onChange={(e) => {
                    setItemsPerPage(Number(e.target.value));
                    setCurrentPage(1); // Volver a la primera página
                  }}
                  className="border rounded px-2 py-1 text-sm"
                >
                  <option value={5}>5</option>
                  <option value={10}>10</option>
                  <option value={20}>20</option>
                  <option value={50}>50</option>
                </select>
              </div>
            </div>
          )}
        </Card>
      </div>

      {/* System Status */}
      <Card className="p-6 mt-6">
        <h3 className="text-lg font-semibold text-gray-900 mb-4">
          Estado del Sistema
        </h3>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div className="flex items-center p-3 bg-blue-50 rounded-lg">
            <div className="w-8 h-8 bg-blue-100 rounded-full flex items-center justify-center mr-3">
              <CheckCircle className="h-4 w-4 text-blue-600" />
            </div>
            <div>
              <p className="font-medium">API Backend</p>
              <p className="text-sm text-gray-600">Operacional</p>
            </div>
          </div>

          <div className="flex items-center p-3 bg-green-50 rounded-lg">
            <div className="w-8 h-8 bg-green-100 rounded-full flex items-center justify-center mr-3">
              <CheckCircle className="h-4 w-4 text-green-600" />
            </div>
            <div>
              <p className="font-medium">Base de Datos</p>
              <p className="text-sm text-gray-600">Conectada</p>
            </div>
          </div>

          <div className="flex items-center p-3 bg-yellow-50 rounded-lg">
            <div className="w-8 h-8 bg-yellow-100 rounded-full flex items-center justify-center mr-3">
              <AlertCircle className="h-4 w-4 text-yellow-600" />
            </div>
            <div>
              <p className="font-medium">Notificaciones</p>
              <p className="text-sm text-gray-600">3 pendientes</p>
            </div>
          </div>
        </div>
      </Card>

      {/* Modal de notificaciones */}
      <Dialog
        open={notificationModalOpen}
        onOpenChange={setNotificationModalOpen}
      >
        <DialogContent className="sm:max-w-[500px]">
          <DialogHeader>
            <DialogTitle>Enviar Notificación</DialogTitle>
            <DialogDescription>
              Envía un mensaje a uno o varios negocios
            </DialogDescription>
          </DialogHeader>

          <NotificationModalContent
            owners={owners || []}
            onClose={() => setNotificationModalOpen(false)}
          />
        </DialogContent>
      </Dialog>

      {/* ============ SECCIÓN DE CAMBIO DE CONTRASEÑA ============ */}
      <div className="mt-8">
        <AdminPasswordChange />
      </div>
      {/* ============ FIN SECCIÓN CAMBIO DE CONTRASEÑA ============ */}
    </AdminLayout>
  );
}
